import React, { useState, useRef, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { FaExternalLinkAlt, FaTimes } from "react-icons/fa";
import { Link } from "react-scroll";
const staticProjects = [
  { _id: "1", title: "E-Commerce Platform", category: "Web", desc: "Full-stack e-commerce solution with payment gateway, inventory management, and admin dashboard.", tag: "Live Project", color: "#0ea5e9", client: "Fashion Hub", duration: "2 Months", image: "/images/web-dev.jpeg" },
  { _id: "2", title: "Food Delivery App", category: "Mobile", desc: "Cross-platform Flutter app for food ordering with real-time tracking and payment integration.", tag: "App Store", color: "#8b5cf6", client: "QuickBite", duration: "3 Months", image: "/images/mobile-app.jpeg" },
  { _id: "3", title: "Digital Marketing Campaign", category: "Marketing", desc: "360° digital marketing campaign that increased brand awareness by 400% and leads by 250%.", tag: "Ongoing", color: "#ef4444", client: "TechStart India", duration: "6 Months", image: "/images/digital-marketing.jpeg" },
  { _id: "4", title: "Business Strategy Consulting", category: "Strategy", desc: "Complete business strategy overhaul including market research, competitor analysis, and growth roadmap.", tag: "Completed", color: "#f59e0b", client: "Verma Enterprises", duration: "1 Month", image: "/images/business-strategy.jpeg" },
  { _id: "5", title: "Corporate Website", category: "Web", desc: "Modern corporate website with CMS, blog, and lead generation forms for a construction company.", tag: "Live", color: "#10b981", client: "Singh Constructions", duration: "6 Weeks", image: "/images/realeste.jpeg" },
  { _id: "6", title: "Social Media Growth", category: "Social Media", desc: "Complete social media management that grew Instagram following from 500 to 50,000 in 6 months.", tag: "Ongoing", color: "#06b6d4", client: "Fashion Hub", duration: "6 Months", image: "/images/social media management.jpeg" },
  { _id: "7", title: "Job Portal Platform", category: "Job Portal", desc: "Full-featured job portal connecting employers and job seekers with AI-powered matching.", tag: "Live", color: "#a855f7", client: "Global Recruiters", duration: "3 Months", image: "/images/hiring.jpeg" },
  { _id: "8", title: "Web Hosting Setup", category: "Hosting", desc: "Complete hosting infrastructure setup with SSL, CDN, and 99.9% uptime guarantee.", tag: "Completed", color: "#f97316", client: "EduTech Solutions", duration: "1 Week", image: "/images/domain-hosting.jpeg" },
];

const categories = ["All", "Web", "Mobile", "Marketing", "Strategy", "Hosting", "Social Media", "Job Portal"];

export default function Portfolio() {
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);
  const [projects, setProjects] = useState([]);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  useEffect(() => { setProjects(staticProjects); }, []);

  const filtered = active === "All" ? projects : projects.filter((p) => p.category === active);

  return (
    <section id="portfolio" className="py-8 sm:py-12 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 sm:w-[600px] h-72 sm:h-[600px] bg-blue-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          ref={ref}         
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-12"
        >
          <span className="text-blue-400 font-semibold text-xs sm:text-sm uppercase tracking-widest">Our Work</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-3 mb-4">
            Our <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
            Explore our portfolio of successful projects that have helped businesses achieve their digital transformation goals.
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12"
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                active === cat
                  ? "gradient-bg text-white shadow-lg shadow-blue-500/30"
                  : "glass text-gray-400 hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Projects Horizontal Scroll */}
        <div className="relative">
          <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
            <AnimatePresence>
              {filtered.map((project, i) => (
                <motion.div
                  key={project._id}
                  layout
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  onClick={() => setSelected(project)}
                  className="glass rounded-3xl overflow-hidden card-hover group cursor-pointer flex-shrink-0 snap-start flex flex-col"
                  style={{ width: "300px", height: "400px" }}
                >
                  <div
                    className="h-48 relative overflow-hidden flex-shrink-0"
                  >
                    <img 
                      src={project.image?.startsWith('http') ? project.image : `${process.env.REACT_APP_API_URL}${project.image}`}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="w-full h-full flex items-center justify-center text-6xl font-bold text-white hidden"
                      style={{ background: `linear-gradient(135deg, ${project.color}60, ${project.color}40)` }}
                    >
                      {project.title.charAt(0)}
                    </div>
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
                    <div
                      className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm"
                      style={{ background: `${project.color}90`, color: 'white', border: `1px solid ${project.color}` }}
                    >
                      {project.category}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-16"
                      style={{ background: `linear-gradient(to top, rgba(10,15,30,0.9), transparent)` }} />
                  </div>

                  <div className="p-5 flex flex-col flex-grow">
                    <h3 className="text-lg font-black text-white mb-1.5">{project.title}</h3>
                    <p className="text-gray-400 text-xs leading-relaxed mb-4 flex-grow">{project.desc}</p>
                    <div className="flex items-center justify-between gap-3 mt-auto">
                      <span
                        className="text-xs px-3 py-1.5 rounded-full font-semibold whitespace-nowrap"
                        style={{ background: `${project.color}20`, color: project.color, border: `1px solid ${project.color}30` }}
                      >
                        {project.tag}
                      </span>
                      <motion.button
                        whileHover={{ x: 4 }}
                        className="text-xs font-bold flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all whitespace-nowrap"
                        style={{ color: project.color, background: `${project.color}15` }}
                      >
                        View <FaExternalLinkAlt className="text-xs" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <div className="absolute top-0 right-0 w-16 h-full pointer-events-none"
            style={{ background: "linear-gradient(to left, rgba(5,8,20,0.8), transparent)" }} />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center mt-10 sm:mt-12"
        >
          <Link to="contact" smooth duration={600} offset={-80}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary px-8 sm:px-10 py-3 sm:py-4 rounded-full text-white font-semibold text-sm sm:text-base"
            >
              <span>Start Your Project</span>
            </motion.button>
          </Link>
        </motion.div>
      </div>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 30 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm glass rounded-3xl overflow-hidden shadow-2xl"
              style={{ background: "rgba(10,15,30,0.97)", border: `1px solid ${selected.color}40` }}
            >
              <div className="h-36 relative overflow-hidden"
                style={{ background: `${selected.color}18` }}>
                <img 
                  src={selected.image?.startsWith('http') ? selected.image : `${process.env.REACT_APP_API_URL}${selected.image}`}
                  alt={selected.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="w-full h-full flex items-center justify-center text-5xl font-bold text-white hidden"
                  style={{ background: `linear-gradient(135deg, ${selected.color}60, ${selected.color}40)` }}
                >
                  {selected.title.charAt(0)}
                </div>
                <div className="absolute inset-0 bg-black/20" />
                <button onClick={() => setSelected(null)}
                  className="absolute top-3 right-3 w-8 h-8 glass rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                  <FaTimes className="text-xs" />
                </button>
                <div className="absolute bottom-3 left-4 px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm"
                  style={{ background: `${selected.color}90`, color: 'white', border: `1px solid ${selected.color}` }}>
                  {selected.category}
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-white font-black text-xl mb-2">{selected.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-5">{selected.desc}</p>

                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="glass rounded-xl p-3">
                    <p className="text-gray-500 text-xs mb-1">Client</p>
                    <p className="text-white text-xs font-semibold">{selected.client}</p>
                  </div>
                  <div className="glass rounded-xl p-3">
                    <p className="text-gray-500 text-xs mb-1">Duration</p>
                    <p className="text-white text-xs font-semibold">{selected.duration}</p>
                  </div>
                </div>

                <div className="rounded-xl px-4 py-2 mb-5 inline-block"
                  style={{ background: `${selected.color}20`, border: `1px solid ${selected.color}40` }}>
                  <span className="font-bold text-sm" style={{ color: selected.color }}>{selected.tag}</span>
                </div>

                <Link to="contact" smooth duration={600} offset={-80} onClick={() => setSelected(null)}>
                  <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    className="w-full py-3 rounded-xl text-white font-semibold text-sm"
                    style={{ background: selected.color }}>
                    Get Similar Project
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
